import { ChevronLeft, Lock, X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Mobile primitives for the AC and AF apps.
 *
 * These target a 360 px base, matching the Figma Client and Prestataire pages.
 * Three constraints run through all of them:
 *  - 100dvh, never 100vh: mobile browser chrome resizes the viewport
 *  - safe-area insets, so nothing sits under the iOS home indicator
 *  - at least 48 px touch targets, which the dense designs do not give for free
 */

/**
 * Full-height mobile frame, clamped so it stays readable on a desktop browser.
 *
 * `fill` pins the frame to exactly one viewport so nothing scrolls but the
 * region that is meant to. A shell with a fixed bottom bar must not also let
 * the document scroll: the two fight, the bar drifts by a few pixels as the
 * page settles, and taps near it land on whatever moved underneath.
 */
export function MobileFrame({
  children,
  className,
  fill = false,
}: {
  children: ReactNode
  className?: string
  fill?: boolean
}) {
  return (
    <div
      className={cn(
        'flex justify-center bg-slate-100',
        fill ? 'h-dvh overflow-hidden' : 'min-h-dvh',
      )}
    >
      <div
        className={cn(
          'relative flex w-full max-w-[430px] flex-col bg-white shadow-sm',
          fill ? 'h-dvh' : 'min-h-dvh',
          className,
        )}
      >
        {children}
      </div>
    </div>
  )
}

export type ScreenHeaderProps = {
  title: string
  onBack?: (() => void) | undefined
  action?: ReactNode
  /** Brand-coloured header, as the design uses on primary screens. */
  tone?: 'light' | 'brand'
  /**
   * The AF detail screens' own header: the title centred, bold, uppercase and
   * brand-coloured, with the back arrow floating beside it rather than pushing
   * it along (frames `4639:1266`, `4546:1000`, `4767:1816`).
   */
  emphasis?: boolean
}

export function ScreenHeader({
  title,
  onBack,
  action,
  tone = 'light',
  emphasis = false,
}: ScreenHeaderProps) {
  const brand = tone === 'brand'
  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 px-2 pt-[env(safe-area-inset-top)]',
        brand ? 'bg-brand-800 text-white' : 'border-b border-slate-200 bg-white text-slate-900',
      )}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour"
          className={cn(
            'grid size-12 place-items-center rounded-full',
            brand ? 'hover:bg-white/10' : 'hover:bg-slate-100',
          )}
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </button>
      ) : (
        <span className="w-2" />
      )}
      <h1
        className={cn(
          'min-w-0 flex-1 truncate',
          emphasis
            ? // -ml-12 pulls the title back over the arrow's own width so it is
              // centred on the screen rather than on the space left beside it.
              'text-brand-600 -ml-12 text-center text-base font-bold uppercase'
            : 'text-base font-semibold',
        )}
      >
        {title}
      </h1>
      {action}
    </header>
  )
}

export type TabItem = {
  to: string
  label: string
  icon: ReactNode
  badge?: number | undefined
  /**
   * Set when this destination belongs to a later sprint than the build shows.
   * The tab stays — and stays reachable, landing on a screen that explains
   * itself — but reads as pending rather than available.
   */
  locked?: number | undefined
}

/**
 * Bottom navigation, matching the AC/AF frames: four labelled destinations
 * with a raised circular action between the second and third.
 *
 * The raised button is a peer of the tabs visually but not semantically — it
 * opens a menu rather than navigating — so it is rendered outside the list and
 * given its own label.
 */
export function TabBar({
  items,
  activePath,
  onNavigate,
  action,
}: {
  items: TabItem[]
  activePath: string
  onNavigate: (to: string) => void
  /** The raised centre button. Omitted where the design has none. */
  action?: { label: string; icon: ReactNode; onPress: () => void } | undefined
}) {
  // The frames place it between the second and third destinations.
  const split = Math.ceil(items.length / 2)

  const tab = (item: TabItem) => {
    const active = activePath === item.to || activePath.startsWith(item.to + '/')
    return (
      <li key={item.to} className="flex-1">
        <button
          type="button"
          onClick={() => onNavigate(item.to)}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'group relative flex h-[62px] w-full flex-col items-center justify-center gap-1.5',
            'transition-colors duration-[--duration-fast] ease-[--ease-out-soft]',
            // brand-600 rather than the frame's brand-500: #1972f7 on white at
            // 14px measures 4.38:1, just under the 4.5:1 the gate requires.
            active ? 'text-brand-600' : 'text-slate-700',
            // slate-500 rather than a lighter grey: 7.0:1 on white, so a
            // pending tab still passes the contrast gate. Locked is a state,
            // not permission to make text unreadable.
            item.locked ? 'text-slate-500' : '',
          )}
        >
          <span
            className={cn(
              'relative transition-transform duration-[--duration-base] ease-[--ease-out-soft]',
              // The icon lifts a little on the tab you are on, and gives under
              // the finger on the one you are reaching for. Small, because this
              // is the control the user touches most in the app.
              active ? '-translate-y-0.5 scale-105' : 'group-active:scale-90',
            )}
          >
            {item.icon}
            {item.badge ? (
              // The frame shows an unlabelled dot, not a count: at a glance
              // "something is waiting" is the whole message, and a number here
              // is unreadable at 12px anyway. It pops in, because it appearing
              // *is* the notification.
              <span
                className="bg-danger animate-pop absolute -top-0.5 -right-1 size-3 rounded-full ring-2 ring-white"
                aria-hidden="true"
              />
            ) : null}
            {item.locked ? (
              <span
                className="absolute -top-1 -right-1.5 grid size-3.5 place-items-center rounded-full bg-slate-200 ring-2 ring-white"
                aria-hidden="true"
              >
                <Lock className="size-2 text-slate-600" />
              </span>
            ) : null}
          </span>
          <span className="text-sm leading-[17px] font-medium">
            {item.label}
            {item.badge ? <span className="sr-only"> ({item.badge} en attente)</span> : null}
            {item.locked ? (
              <span className="sr-only"> (prévu pour le sprint {item.locked})</span>
            ) : null}
          </span>
        </button>
      </li>
    )
  }

  return (
    <nav
      aria-label="Navigation principale"
      className="sticky bottom-0 z-20 shrink-0 bg-white pb-[env(safe-area-inset-bottom)]"
    >
      <div className="relative flex items-stretch">
        <ul className="flex flex-1">{items.slice(0, split).map(tab)}</ul>
        {action ? <span className="w-[42px] shrink-0" aria-hidden="true" /> : null}
        <ul className="flex flex-1">{items.slice(split).map(tab)}</ul>

        {action ? (
          <button
            type="button"
            onClick={action.onPress}
            aria-label={action.label}
            className={cn(
              'bg-brand-500 absolute -top-6 left-1/2 grid size-[42px] -translate-x-1/2',
              'place-items-center rounded-full text-white shadow-lg',
            )}
          >
            {action.icon}
          </button>
        ) : null}
      </div>
    </nav>
  )
}

/**
 * Bottom sheet. The mobile designs use these heavily for pickers — vehicle
 * type, zone, payment method. Built on the platform dialog so focus trapping
 * and Escape-to-close come for free rather than being hand-rolled badly.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  /**
   * Turns the header into the picker sheets' layout (AC frame `5731:19137`):
   * the title moves left, the subtitle sits under it, and a close X appears on
   * the right. The plain sheets have no subtitle and stay centred.
   */
  subtitle?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // Backdrop closes; the panel itself must not.
        if (e.target === ref.current) onClose()
      }}
      aria-label={title}
      // The sheet travels up from the edge it belongs to and the backdrop
      // fades with it, so it reads as arriving from off-screen rather than
      // appearing on top of everything at once. `open:` is what makes this
      // fire: a <dialog> gets the attribute the moment showModal() runs.
      className={cn(
        'mx-auto mt-auto mb-0 w-full max-w-[430px] rounded-t-2xl p-0',
        'open:animate-sheet-in open:backdrop:animate-fade-in backdrop:bg-black/40',
      )}
    >
      <div className="flex max-h-[80dvh] flex-col">
        {/* The frames give a sheet a grab handle and a centred title, with no
            close button — the backdrop dismisses it. The handle is made the
            button so there is still a real, focusable control to close with,
            without adding chrome the design does not have. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="group flex shrink-0 justify-center px-4 pt-6 pb-3"
        >
          <span
            className="h-1 w-12 rounded-full bg-slate-300 transition-[width,background-color] duration-[--duration-fast] ease-[--ease-out-soft] group-hover:w-14 group-hover:bg-slate-400 group-active:w-10"
            aria-hidden="true"
          />
        </button>

        {subtitle ? (
          <div className="flex shrink-0 items-start gap-3 px-6">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-slate-700">{title}</h2>
              <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            </div>
            {/* The frame draws an explicit X here as well as the handle. Both
                close it; the handle is what a thumb reaches, the X is what a
                pointer aims at. */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="-mt-1 grid size-9 shrink-0 place-items-center rounded-full hover:bg-slate-100"
            >
              <X className="size-6 text-slate-700" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <h2 className="shrink-0 px-6 text-center text-base font-bold text-slate-700">{title}</h2>
        )}

        <div className="overflow-y-auto px-6 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </dialog>
  )
}

/** Tappable row — the dominant layout unit across both mobile designs. */
export function ListItem({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  className,
}: {
  leading?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  trailing?: ReactNode
  onClick?: (() => void) | undefined
  className?: string
}) {
  const content = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-medium text-slate-900">{title}</span>
        {subtitle ? (
          <span className="block truncate text-xs text-slate-500">{subtitle}</span>
        ) : null}
      </span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </>
  )

  const base = cn('flex min-h-14 w-full items-center gap-3 px-4 py-3', className)

  return onClick ? (
    <button type="button" onClick={onClick} className={cn(base, 'text-left hover:bg-slate-50')}>
      {content}
    </button>
  ) : (
    <div className={base}>{content}</div>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      {icon ? <span className="text-slate-300">{icon}</span> : null}
      <p className="text-sm font-medium text-slate-900">{title}</p>
      {description ? <p className="max-w-xs text-xs text-slate-500">{description}</p> : null}
      {action ? <span className="mt-2">{action}</span> : null}
    </div>
  )
}

/** Sticky action area for a screen's primary call to action. */
export function StickyActions({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      {children}
    </div>
  )
}
